import React, { useCallback, useEffect, useState, type JSX } from 'react';
import Table, { type TableColumn } from '@resources/js/components/Table/Table';
import useTheme from '@resources/js/contexts/useTheme';
import VerticalContent from '@resources/js/components/VerticalContent/VerticalContent';
import EnhancedMarkdown from '@resources/js/components/EnhancedMarkdown/EnhancedMarkdown';
import {
  IconDefaultsContextProvider,
  iconSizeLarge,
} from '@resources/js/contexts/useIconDefaults';
import Select from '@resources/js/components/Select/Select';
import { type SelectListItem } from '@resources/js/typings/viewModels/selectListItemViewModel';
import BuildsNoDataPlaceholder from '../BuildsNoDataPlaceholder/BuildsNoDataPlaceholder';
import WarningParagraph from '@resources/js/components/WarningParagraph/WarningParagraph';
import buildsEnhancedMarkdownOverrides from '@resources/js/helpers/buildsEnhancedMarkdownOverrides';

type TableViewModel = {
  columns: Record<
    'item' | 'dps' | 'popularity',
    BuildsTrinketCombosSectionViewModelColumn
  > &
    Partial<
      Record<'maxKey' | 'hps', BuildsTrinketCombosSectionViewModelColumn>
    >;
  data: Array<{
    item: string;
    maxKey?: string;
    dps: string;
    hps?: string;
    popularity: string;
  }>;
};

type FilteredTableViewModel = {
  description: string;
  table: TableViewModel;
};

export type BuildsTrinketCombosSectionViewModel = {
  title: string;
  description: string;
  warning: string | null;
  defaultTable: TableViewModel;
  filteredTables: FilteredTableViewModel[];
  trinketOptions: SelectListItem[];
  filterDropdownTitle: string;
  filterDropdownDescription: string;
};

type BuildsTrinketCombosSectionViewModelColumn = Pick<
  TableColumn,
  'header' | 'textAlign' | 'noWrap'
>;

const iconDefaults = {
  select: {
    icon: { bordered: true, rounded: false },
  },
  table: {
    item: { size: iconSizeLarge },
  },
};

export default function BuildsTrinketCombosSection({
  title,
  description,
  warning,
  defaultTable,
  filteredTables,
  trinketOptions,
  filterDropdownTitle,
  filterDropdownDescription,
}: BuildsTrinketCombosSectionViewModel): JSX.Element {
  const theme = useTheme();
  const [selectedTrinket, setSelectedTrinket] = useState(0);

  useEffect(() => {
    setSelectedTrinket(0);
  }, [trinketOptions]);

  const noData = defaultTable.data.length === 0;

  const trinketComboDescription =
    selectedTrinket === 0 || !filteredTables[selectedTrinket]
      ? null
      : filteredTables[selectedTrinket].description;
  const trinketComboTable =
    selectedTrinket === 0 || !filteredTables[selectedTrinket]
      ? defaultTable
      : filteredTables[selectedTrinket].table;
  const onChange = useCallback(
    (option: { value: number } | null) =>
      setSelectedTrinket(option?.value ?? 0),
    [setSelectedTrinket],
  );
  const selectedTrinketValue = trinketOptions.find(
    (option) => option.value === selectedTrinket,
  );

  return (
    <VerticalContent className={'builds-trinket-combos-section'}>
      <EnhancedMarkdown
        markdown={`
# ${title}

${description}
`}
        overrides={buildsEnhancedMarkdownOverrides}
      />
      {noData ? (
        <BuildsNoDataPlaceholder />
      ) : (
        <>
          <div>
            <span className={'builds-trinket-combos-section__filter'}>
              {filterDropdownTitle}
            </span>
            <span
              className={'builds-trinket-combos-section__filter-description'}
            >
              <EnhancedMarkdown markdown={`${filterDropdownDescription}:`} />
            </span>
            <div className={'builds-trinket-combos-section__select'}>
              <IconDefaultsContextProvider overrides={iconDefaults.select}>
                <Select
                  options={trinketOptions}
                  value={selectedTrinketValue}
                  onChange={onChange}
                  usesEnhancedMarkdown
                  maxMenuHeight={300}
                  inputId='trinket-select'
                />
              </IconDefaultsContextProvider>
            </div>
          </div>
          {trinketComboDescription ? (
            <EnhancedMarkdown markdown={trinketComboDescription} />
          ) : null}
          {warning && (
            <WarningParagraph>
              <EnhancedMarkdown markdown={warning} wrapped={false} />
            </WarningParagraph>
          )}
          <IconDefaultsContextProvider overrides={iconDefaults.table}>
            <Table
              {...{
                ...trinketComboTable,
                columns: {
                  ...trinketComboTable.columns,
                  item: {
                    ...trinketComboTable.columns.item,
                    maxWidth: 230,
                  },
                },
              }}
              enhancedMarkdownEnabled={true}
              truncate={{
                limit: 6,
                showMoreButton: { styles: theme.primaryButtonStyles },
              }}
            />
          </IconDefaultsContextProvider>
        </>
      )}
    </VerticalContent>
  );
}
